import { Badge, Button, cn, ModeToggle, Skeleton } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Circle, Lock, LogOut, Minus, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useCompetitionAuth } from "@/modules/auth/presentation/model/useCompetitionAuth";
import type { Team } from "@/modules/competition/presentation/api/client";
import {
  useDivisionsQuery,
  useSettingsQuery,
  useTeamsQuery,
} from "@/modules/competition/presentation/api/queries";
import { useEventSummaryQuery } from "@/modules/events/presentation/api/queries";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import type { BaseScoreEntry } from "../api/client";
import { useJudgeScoresQuery, useSaveScore } from "../api/queries";

// ─── Config ───────────────────────────────────────────────────────────────────

type Criterion = { key: string; label: string; max: number; isExtra?: boolean };
type JudgeConfig = {
  label: string;
  subject: string;
  accentClass: string;
  maxTotal: number;
  criteria: Criterion[];
};

const JUDGE_CONFIG: Record<1 | 2 | 3, JudgeConfig> = {
  1: {
    label: "Judge 1",
    subject: "Dance Skill & Technique",
    accentClass: "text-accent",
    maxTotal: 30,
    criteria: [
      { key: "strengthDifficulty", label: "Strength & Difficulty", max: 10 },
      { key: "timingControl", label: "Timing & Control", max: 10 },
      { key: "unitySynchronicity", label: "Unity & Synchronizity", max: 10 },
    ],
  },
  2: {
    label: "Judge 2",
    subject: "Stage Presence & Performance",
    accentClass: "text-chart-3",
    maxTotal: 30,
    criteria: [
      { key: "charismaConfidence", label: "Charisma & Confidence", max: 10 },
      {
        key: "facialExpression",
        label: "Facial Expression & Emotions",
        max: 10,
      },
      { key: "stageAwareness", label: "Stage Awareness & Engagement", max: 10 },
    ],
  },
  3: {
    label: "Judge 3",
    subject: "Concept & Creativity",
    accentClass: "text-chart-5",
    maxTotal: 40,
    criteria: [
      { key: "conceptOriginality", label: "Concept & Originality", max: 15 },
      {
        key: "costumeVisual",
        label: "Costume, Props & Visual Composition",
        max: 15,
      },
      {
        key: "choreographyQuality",
        label: "Choreography Creativity & Overall Quality",
        max: 10,
      },
    ],
  },
};

// ─── Types ────────────────────────────────────────────────────────────────────

type CriteriaValues = Record<string, number>;
type ScoreEntry = BaseScoreEntry &
  Record<string, number | string | null | Record<string, number> | undefined>;

// ─── Stepper ──────────────────────────────────────────────────────────────────

function Stepper({
  value,
  max,
  onChange,
  disabled,
}: {
  value: number;
  max: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="size-10 shrink-0"
        onClick={() => onChange(Math.max(0, value - 1))}
        disabled={disabled || value <= 0}
      >
        <Minus className="size-3.5" />
      </Button>
      <input
        type="number"
        min={0}
        max={max}
        value={value}
        onChange={(e) => {
          const v = Math.min(
            max,
            Math.max(0, Number.parseInt(e.target.value, 10) || 0),
          );
          onChange(v);
        }}
        disabled={disabled}
        className="w-10 border-border border-b bg-transparent text-center font-bold text-foreground text-lg tabular-nums focus:outline-none disabled:opacity-50"
      />
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="size-10 shrink-0"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={disabled || value >= max}
      >
        <Plus className="size-3.5" />
      </Button>
      <span className="w-8 text-muted-foreground text-sm">/{max}</span>
    </div>
  );
}

// ─── ScoreFormCard ────────────────────────────────────────────────────────────

function initFromScore(
  criteria: Criterion[],
  score: ScoreEntry | null,
): CriteriaValues {
  const result: CriteriaValues = {};
  for (const c of criteria) {
    result[c.key] =
      c.isExtra && score?.extraScores
        ? (score.extraScores[c.key] ?? 0)
        : typeof score?.[c.key] === "number"
          ? (score[c.key] as number)
          : 0;
  }
  return result;
}

function ScoreFormCard({
  eventId,
  team,
  score,
  criteria,
  maxTotal,
  isOpen,
  judgeNumber,
  roundIndex,
}: {
  eventId: string;
  team: Team;
  score: ScoreEntry | null;
  criteria: Criterion[];
  maxTotal: number;
  isOpen: boolean;
  judgeNumber: 1 | 2 | 3;
  roundIndex: number;
}) {
  const initial = initFromScore(criteria, score);
  const [values, setValues] = useState<CriteriaValues>(initial);
  const [savedValues, setSavedValues] = useState<CriteriaValues>(initial);
  const saveMutation = useSaveScore(eventId, judgeNumber, roundIndex);

  useEffect(() => {
    const next = initFromScore(criteria, score);
    setValues(next);
    setSavedValues(next);
  }, [team.id, roundIndex, score, criteria]);

  const total = useMemo(
    () => Object.values(values).reduce((sum, v) => sum + v, 0),
    [values],
  );

  const isDirty = JSON.stringify(values) !== JSON.stringify(savedValues);
  const isSaved =
    !isDirty &&
    (score?.submittedAt !== null ||
      JSON.stringify(savedValues) !==
        JSON.stringify(initFromScore(criteria, null)));

  const handleSave = async () => {
    const extraScores: Record<string, number> = {};
    const data: Record<string, number | Record<string, number>> = {};
    for (const c of criteria) {
      if (c.isExtra) extraScores[c.key] = values[c.key] ?? 0;
      else data[c.key] = values[c.key] ?? 0;
    }
    if (Object.keys(extraScores).length > 0) {
      data.extraScores = extraScores;
    }
    await saveMutation.mutateAsync({ teamId: team.id, data });
    setSavedValues({ ...values });
  };

  const set = (key: string, val: number) =>
    setValues((prev) => ({ ...prev, [key]: val }));

  const pct = maxTotal > 0 ? (total / maxTotal) * 100 : 0;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl bg-card ring-1 transition-all",
        isSaved ? "ring-primary/60" : "ring-border",
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-border border-b px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="flex size-7 items-center justify-center rounded-full bg-muted font-bold text-muted-foreground text-xs">
            {team.orderNumber}
          </span>
          <span className="font-semibold text-foreground">{team.name}</span>
        </div>
        {isSaved ? (
          <Badge variant="default" className="gap-1 text-xs">
            <CheckCircle2 className="size-3" />
            ບັນທຶກແລ້ວ
          </Badge>
        ) : isDirty ? (
          <Badge variant="secondary" className="text-xs">
            <Circle className="mr-1 size-2 fill-current" />
            ຍັງບໍ່ໄດ້ບັນທຶກ
          </Badge>
        ) : null}
      </div>

      {/* Criteria rows */}
      <div className="divide-y divide-border">
        {criteria.map((c) => (
          <div
            key={c.key}
            className="flex items-center justify-between px-4 py-3.5"
          >
            <span className="text-foreground text-sm">{c.label}</span>
            <Stepper
              value={values[c.key] ?? 0}
              max={c.max}
              onChange={(v) => set(c.key, v)}
              disabled={!isOpen}
            />
          </div>
        ))}
      </div>

      {/* Score progress bar */}
      <div className="h-1 w-full bg-muted">
        <div
          className={cn(
            "h-full transition-all",
            pct >= 80
              ? "bg-primary"
              : pct >= 50
                ? "bg-chart-3"
                : "bg-muted-foreground/40",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-3">
        <div className="tabular-nums">
          <span className="text-muted-foreground text-sm">ລວມ: </span>
          <span
            className={cn(
              "font-extrabold text-2xl",
              pct === 100 ? "text-primary" : "text-foreground",
            )}
          >
            {total}
          </span>
          <span className="text-muted-foreground text-sm"> / {maxTotal}</span>
        </div>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!isDirty || !isOpen || saveMutation.isPending}
        >
          {saveMutation.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
        </Button>
      </div>
    </div>
  );
}

// ─── JudgePage ────────────────────────────────────────────────────────────────

export function JudgePage({
  eventId,
  judgeNumber,
}: {
  eventId: string;
  judgeNumber: 1 | 2 | 3;
}) {
  const config = JUDGE_CONFIG[judgeNumber];
  const { user, signOut } = useCompetitionAuth();
  const navigate = useNavigate();
  const { data: eventInfo } = useEventSummaryQuery(eventId);

  const { data: divisions, isLoading: divLoading } = useDivisionsQuery(eventId);
  const sortedDivisions = useMemo(
    () => [...(divisions ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [divisions],
  );

  const [pickedDivisionId, setPickedDivisionId] = useState("");
  const [pickedRound, setPickedRound] = useState(1);
  useEffect(() => {
    if (!sortedDivisions.length) return;
    setPickedDivisionId((curr) => {
      if (curr && sortedDivisions.some((d) => d.id === curr)) return curr;
      return sortedDivisions[0].id;
    });
  }, [sortedDivisions]);

  const { data: settings } = useSettingsQuery(eventId);
  useEffect(() => {
    setPickedRound(1);
  }, [pickedDivisionId]);

  const pickedDivision = useMemo(
    () =>
      sortedDivisions.find((d) => d.id === pickedDivisionId) ??
      sortedDivisions[0],
    [sortedDivisions, pickedDivisionId],
  );

  const maxRoundsForDivision = pickedDivision?.roundCount ?? 1;
  const criteriaForPickedDivision = useMemo(() => {
    const judgeRole = `judge${judgeNumber}`;
    const extraCriteria =
      pickedDivision?.extraCriteria
        ?.filter((c) => c.judgeRole === judgeRole)
        .sort((a, b) =>
          a.sortOrder === b.sortOrder
            ? a.createdAt.localeCompare(b.createdAt)
            : a.sortOrder - b.sortOrder,
        )
        .map(
          (c): Criterion => ({
            key: c.id,
            label: c.name,
            max: c.maxScore,
            isExtra: true,
          }),
        ) ?? [];
    return [...config.criteria, ...extraCriteria];
  }, [config.criteria, judgeNumber, pickedDivision]);
  const maxScoreForPickedDivision = useMemo(
    () => criteriaForPickedDivision.reduce((sum, c) => sum + c.max, 0),
    [criteriaForPickedDivision],
  );

  useEffect(() => {
    setPickedRound((r) =>
      Number.isFinite(r) && r >= 1 ? Math.min(r, maxRoundsForDivision) : 1,
    );
  }, [maxRoundsForDivision]);

  const { data: teams, isLoading: teamsLoading } = useTeamsQuery(
    eventId,
    pickedDivisionId,
    {
      enabled: Boolean(eventId) && Boolean(pickedDivisionId) && !divLoading,
      roundIndex: pickedRound,
    },
  );
  const { data: scores, isLoading: scoresLoading } = useJudgeScoresQuery(
    eventId,
    judgeNumber,
    pickedRound,
  );

  const isOpen = settings?.isOpen === true;
  const settingsReady = settings !== undefined;

  const scoreMap = useMemo(() => {
    const map = new Map<string, ScoreEntry>();
    for (const s of scores ?? []) {
      map.set(s.teamId, s as ScoreEntry);
    }
    return map;
  }, [scores]);

  const teamIdSet = useMemo(
    () => new Set((teams ?? []).map((t) => t.id)),
    [teams],
  );
  const submittedCount = useMemo(
    () =>
      scores?.filter(
        (s) =>
          teamIdSet.has(s.teamId) &&
          s.submittedAt !== null &&
          s.submittedAt !== undefined,
      ).length ?? 0,
    [scores, teamIdSet],
  );

  const handleSignOut = async () => {
    await signOut();
    navigate({
      to: "/competition/judge-login/$eventId/$judgeNumber",
      params: { eventId, judgeNumber: String(judgeNumber) },
    });
  };

  const awaitingDivisionPick = sortedDivisions.length > 0 && !pickedDivisionId;
  const isLoading =
    divLoading ||
    awaitingDivisionPick ||
    (pickedDivisionId ? teamsLoading : false) ||
    scoresLoading;
  const totalTeams = teams?.length ?? 0;

  const judgeDisplayLabel =
    judgeNumber === 1
      ? eventInfo?.judgeDisplayNames?.judge1
      : judgeNumber === 2
        ? eventInfo?.judgeDisplayNames?.judge2
        : eventInfo?.judgeDisplayNames?.judge3;

  const roundPickerOptions = useMemo(
    () =>
      Array.from({ length: maxRoundsForDivision }, (_, i) => ({
        value: String(i + 1),
        label: `ຮອບ ${i + 1}`,
      })),
    [maxRoundsForDivision],
  );

  const divisionPickerOptions = useMemo(
    () =>
      sortedDivisions.map((d) => ({
        value: d.id,
        label: d.name,
      })),
    [sortedDivisions],
  );

  return (
    <div className="flex min-h-svh flex-col bg-background">
      {/* Sticky Header */}
      <header className="kpop-bg sticky top-0 isolate z-10 border-border border-b bg-card text-foreground shadow-[0_8px_32px_-12px_rgba(0,0,0,0.12)] dark:shadow-black/35">
        {/* Top ribbon */}
        <div className="flex items-center justify-between gap-3 border-border border-b bg-muted/35 px-4 py-2.5 sm:px-5 dark:bg-muted/25">
          <p className="font-bold text-[10px] text-foreground uppercase tracking-[0.22em] sm:text-xs">
            KiDS EXPO 2026
          </p>
          <div className="flex items-center gap-0.5 rounded-full border border-border bg-background/95 p-0.5 shadow-sm dark:bg-background/80">
            <ModeToggle />
            <Button
              variant="ghost"
              size="icon"
              className="size-9 shrink-0 rounded-full"
              onClick={handleSignOut}
              title="ອອກຈາກລະບົບ"
            >
              <LogOut className="size-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-4 px-4 py-4 sm:px-5 sm:py-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
            <div
              className={cn(
                "flex size-16 shrink-0 items-center justify-center rounded-2xl border-2 bg-muted font-black text-xl tabular-nums shadow-md sm:size-[4.25rem]",
                judgeNumber === 1 &&
                  "border-accent/65 text-accent [text-shadow:_0_1px_0_rgb(255_255_255_/_0.5)] dark:[text-shadow:_0_1px_2px_rgb(0_0_0_/_0.85)]",
                judgeNumber === 2 &&
                  "border-chart-3/70 text-chart-3 [text-shadow:_0_1px_0_rgb(255_255_255_/_0.5)] dark:[text-shadow:_0_1px_2px_rgb(0_0_0_/_0.85)]",
                judgeNumber === 3 &&
                  "border-chart-5/70 text-chart-5 [text-shadow:_0_1px_0_rgb(255_255_255_/_0.5)] dark:[text-shadow:_0_1px_2px_rgb(0_0_0_/_0.85)]",
              )}
              aria-hidden
            >
              J{judgeNumber}
            </div>

            <div className="min-w-0 flex-1 space-y-3">
              <div className="rounded-xl border border-border bg-background px-3 py-3 shadow-sm ring-1 ring-black/[0.04] sm:px-4 sm:py-3.5 dark:ring-white/[0.08]">
                <h1 className="kpop-gradient-text wrap-break-word font-extrabold text-xl leading-tight tracking-tight [filter:drop-shadow(0_1px_0_rgb(255_255_255_/0.7))] sm:text-2xl dark:[filter:drop-shadow(0_1px_1px_rgb(0_0_0_/1))_drop-shadow(0_0_12px_rgb(0_0_0_/0.65))]">
                  {eventInfo?.name ?? "…"}
                </h1>
                <div className="mt-2.5 flex flex-wrap items-center gap-2 border-border/70 border-t pt-2.5">
                  {sortedDivisions.length === 1 && pickedDivision ? (
                    <Badge
                      variant="outline"
                      className="border-foreground/20 bg-muted/80 px-2 font-semibold text-foreground text-xs"
                    >
                      ຮາງ: {pickedDivision.name}
                    </Badge>
                  ) : null}
                  {sortedDivisions.length > 1 && pickedDivision ? (
                    <Badge
                      variant="outline"
                      className="border-foreground/25 bg-muted/60 px-2 font-semibold text-foreground text-xs tabular-nums"
                    >
                      {sortedDivisions.length} ຮາງ
                    </Badge>
                  ) : null}
                  {maxRoundsForDivision > 1 ? (
                    <Badge
                      variant="outline"
                      className={cn(
                        "border-2 px-2 font-semibold text-xs shadow-sm dark:shadow-black/40",
                        judgeNumber === 1 &&
                          "border-accent/55 bg-accent/12 text-accent-foreground",
                        judgeNumber === 2 &&
                          "border-chart-3/55 bg-chart-3/12 text-chart-3",
                        judgeNumber === 3 &&
                          "border-chart-5/55 bg-chart-5/12 text-chart-5",
                      )}
                    >
                      ຮອບ {pickedRound} / {maxRoundsForDivision}
                    </Badge>
                  ) : pickedDivision ? (
                    <Badge
                      variant="outline"
                      className="border-foreground/25 bg-muted/70 px-2 font-semibold text-foreground text-xs"
                    >
                      ຮອບດຽວ
                    </Badge>
                  ) : null}
                </div>
              </div>

              {(sortedDivisions.length > 1 || maxRoundsForDivision > 1) && (
                <div className="flex flex-col gap-3 rounded-xl border border-border bg-muted/55 p-3 sm:flex-row sm:flex-wrap sm:items-end dark:bg-muted/35">
                  {sortedDivisions.length > 1 ? (
                    <div className="min-w-0 flex-1 sm:min-w-[200px]">
                      <p className="mb-1.5 font-semibold text-foreground/85 text-xs">
                        ເລືອກຮາງ
                      </p>
                      <SimpleSelect
                        value={pickedDivisionId}
                        onValueChange={setPickedDivisionId}
                        options={divisionPickerOptions}
                        placeholder="ເລືອກຮາງ"
                      />
                    </div>
                  ) : null}
                  {maxRoundsForDivision > 1 ? (
                    <div className="min-w-0 flex-1 sm:min-w-[180px]">
                      <p className="mb-1.5 font-semibold text-foreground/85 text-xs">
                        ເລືອກຮອບໃຫ້ຄະແນນ
                      </p>
                      <SimpleSelect
                        value={String(pickedRound)}
                        onValueChange={(v) =>
                          setPickedRound(Number.parseInt(v, 10) || 1)
                        }
                        options={roundPickerOptions}
                        placeholder="ເລືອກຮອບ"
                      />
                    </div>
                  ) : null}
                </div>
              )}

              <div className="space-y-1">
                {judgeDisplayLabel ? (
                  <p className="wrap-break-word text-foreground/75 text-xs sm:truncate">
                    {judgeDisplayLabel}
                  </p>
                ) : null}
                <p
                  className={cn(
                    "wrap-break-word font-semibold text-sm leading-snug sm:truncate",
                    config.accentClass,
                  )}
                >
                  {config.label} — {config.subject}
                </p>
                <p className="wrap-break-word text-foreground/75 text-xs">
                  ຍິນດີຕ້ອນຮັບ,{" "}
                  <span className="font-semibold text-foreground">
                    {user?.name}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {!isLoading && totalTeams > 0 && (
            <div className="rounded-xl border border-border bg-muted/50 px-3 py-3 dark:bg-muted/30">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="font-semibold text-foreground text-xs">
                  ຄວາມຄືບໜ້າສົ່ງຄະແນນ
                  {maxRoundsForDivision > 1 ? (
                    <span className="font-normal text-foreground/70">
                      {" "}
                      · ຮອບ {pickedRound}
                    </span>
                  ) : null}
                </span>
                <span className="font-medium text-foreground/75 text-xs tabular-nums">
                  {submittedCount}/{totalTeams}
                </span>
              </div>
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-background/70 ring-1 ring-border dark:bg-background/60">
                <div
                  className={cn(
                    "h-full rounded-full transition-all duration-500 ease-out",
                    judgeNumber === 1 && "bg-primary",
                    judgeNumber === 2 && "bg-chart-3",
                    judgeNumber === 3 && "bg-chart-5",
                  )}
                  style={{
                    width: `${(submittedCount / totalTeams) * 100}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Scoring Closed Banner */}
      {settingsReady && !isOpen && (
        <div className="flex items-center gap-2 bg-destructive/10 px-4 py-3 text-destructive text-sm">
          <Lock className="size-4 shrink-0" />
          <span>ການໃຫ້ຄະແນນຖືກປິດຊົ່ວຄາວ — ທ່ານບໍ່ສາມາດແກ້ໄຂຄະແນນໄດ້</span>
        </div>
      )}

      {/* Team List */}
      <main className="flex flex-col gap-4 p-4">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders
            <Skeleton key={i} className="h-56 w-full rounded-2xl" />
          ))
        ) : totalTeams === 0 ? (
          <div className="py-20 text-center text-muted-foreground">
            ຍັງບໍ່ມີທີມໃດ
          </div>
        ) : (
          (teams ?? []).map((team) => (
            <ScoreFormCard
              key={team.id}
              eventId={eventId}
              team={team}
              score={scoreMap.get(team.id) ?? null}
              criteria={criteriaForPickedDivision}
              maxTotal={maxScoreForPickedDivision}
              isOpen={isOpen}
              judgeNumber={judgeNumber}
              roundIndex={pickedRound}
            />
          ))
        )}
      </main>
    </div>
  );
}
